import {
    AuditLogAppEntry,
    AuditLogCompanyEntry,
    AuditLogCrmConfigEntry,
    AuditLogDashboardData,
    AuditLogEmailTemplateEntry,
    AuditLogEntityType,
    AuditLogFilters,
    AuditLogPublicRoleEntry,
    AuditLogRow,
    AuditLogUserEntry
} from '../../types/AuditLogs';
import { getAppointmentDurationLabels } from '../../components/ScheduleGenie/ScheduleGenie.helpers';

const normalizeString = (value?: string | number | boolean | null) => `${value ?? ''}`.trim();
const lowerCase = (value?: string | number | boolean | null) => normalizeString(value).toLowerCase();

const buildSearchValue = (...values: Array<string | number | boolean | null | undefined>) =>
    values
        .map(value => normalizeString(value))
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

const fieldLabelMap: Record<string, string> = {
    appointment_length: 'Select Duration',
    appointment_types_enabled: 'Available Appointment Types',
    body: 'Body',
    call_to_action: 'Call to Action',
    preview_text: 'Preview Text',
    reply_to_email: 'Reply To Email',
    cta_text: 'Call To Action Button Text',
    cta_link: 'Call To Action Link',
    cta_button_text_color: 'Call To Action Button Text Color',
    cta_button_color: 'Call To Action Button Color',
    report_created: 'Report Creation',
    recipients: 'Report Email Recipient',
    recommended_reports: 'Recommended Reports',
    sender_email: 'Sender Email',
    banner_image_url: 'Banner Image',
    header_image_url: 'Header Image',
    sms_alternative: 'SMS Alternative',
    sms_message_create: 'Confirmation Text Message',
    template_name: 'Email Template Name',
    address1: 'Street Line 1',
    address2: 'Street Line 2',
    active_coupon_id: 'Active Coupon',
    active: 'Active',
    accepting_influencers_info: 'Influencer Toggle',
    brand_primary_color: 'Primary Brand Color',
    brand_text_color: 'Font and Icon Color',
    business_category_id: 'Business Category',
    blocklist_email: 'Blocked Email',
    cc_email: 'CC Email',
    city: 'City',
    collapse_threshhold_mobile: 'Mobile: Collapse Threshold',
    company_name: 'Company Name',
    crm_config_id: 'CRM Configuration',
    crm_email: 'CRM Email',
    dma: 'Designated Marketing Area',
    custom_disclaimer: 'Custom Disclaimer',
    collapse_threshhold: 'Desktop: Collapse Threshold',
    cta_hidden: 'Call To Action',
    drip_schedule_name: 'Drip Schedule Name',
    drip_schedule_enabled: 'Drip Schedule',
    entrata_property_id: 'Property Name',
    entrata_lead_source_id: 'Lead Source',
    drip_entry_added: 'Drip Entry',
    drip_entry_enabled: 'Drip Entry',
    drip_entry_time_offset_hours: 'Time After Conversion',
    drip_entry_send_via_email: 'Send via Email',
    drip_entry_send_via_sms: 'Send via SMS',
    drip_entry_template_name: 'Email Template',
    drip_schedule_id: 'Lead Nurturing Drip Schedule',
    blast_name: 'Scheduled Blast Name',
    blast_send_via_email: 'Send via Email',
    blast_send_via_sms: 'Send via SMS',
    blast_sms_message: 'SMS Message',
    blast_email_template_name: 'Email Template',
    blast_date: 'Blast Date',
    blast_time: 'Blast Time',
    blast_list_segments: 'List Segments',
    list_segment_name: 'List Segment Name',
    list_segment_enabled: 'List Segment',
    list_segment_contacts_added: 'Contacts Added',
    list_segment_contacts_imported: 'Contacts Imported',
    desktop_position_adjustment: 'Desktop Position Adjustment',
    email: 'Email',
    email_body_html: 'Email Body',
    enabled: 'Topic Status',
    estimate_multiplier: 'Estimate Multiplier',
    floorplan_added: 'Add a Floorplan',
    floor_plan_name: 'Add a Floorplan (Name)',
    feature_name: 'Feature Name',
    first_name: 'First Name',
    hidden_widgets: 'Desktop Hidden Modules',
    hidden_widgets_mobile: 'Mobile Hidden Modules',
    headline_hidden: 'Headline',
    headline_text: 'Headline',
    host_element_id: 'Host Element ID',
    host_element_parent: 'Host Element Parent',
    host_element_type: 'Host Element Type',
    is_paused: 'ATP',
    is_test: 'Test Company',
    is_universal: 'Universal TCC',
    last_name: 'Last Name',
    lead_notification_cc_emails: 'CC Emails for Lead Notifications',
    lead_notification_primary_emails: 'Include Emails for Lead Notifications',
    live_chat_group_id: 'Live Chat Group ID',
    logo_background_color: 'Logo Background',
    market_rent: 'Add a Floorplan (Market Rent)',
    matchback_import: 'Matchback Import',
    max_appointment_period: 'How Far in Advance',
    minimum_income_ratio: 'Income to Rent Variable',
    mobile_position_adjustment: 'Mobile Position Adjustment',
    notification_bar_active: 'Active',
    notification_bar_background_color: 'Background Color',
    notification_bar_color: 'Text Color',
    notification_bar_end_date: 'End Date',
    notification_bar_link_1: 'Call to Action 1',
    notification_bar_link_2: 'Call to Action 2',
    notification_bar_message: 'Notification Banner Message',
    notification_bar_position: 'Banner Position Setting',
    notification_bar_start_date: 'Start Date',
    optional_logos: 'Optional Logos',
    optional_logos_hidden: 'Optional Logos',
    master_id: 'Odoo Master ID',
    meet_elise_enabled: 'Activate Toggle',
    meet_elise_chat_enabled: 'Chat Toggle',
    meet_elise_tour_enabled: 'Tour Toggle',
    meet_elise_email_enabled: 'Email Toggle',
    meet_elise_text_enabled: 'Text Toggle',
    meet_elise_chat_title: 'Chat Button Title',
    meet_elise_tour_title: 'Tour Button Title',
    meet_elise_email_title: 'Email Button Title',
    meet_elise_text_title: 'Text Button Title',
    name: 'Name',
    override_lead_notification: 'Override Lead Notification',
    period_start_work: 'How Soon Can a Prospect Schedule',
    offer: 'Offer',
    ownership_group_id: 'Ownership Group',
    pass: 'Password',
    phone: 'Phone Number',
    plain_text_lead_notify: 'Lead Notification Email Format',
    property_management_company_id: 'Property Management Company',
    region_id: 'Region',
    role_id: 'Role',
    secondary_image_url: 'Secondary Image',
    show_as_standalone: 'Enable Standalone Widget',
    show_on_top: 'Show Widget On Top',
    schedule_created: 'Create Schedule',
    start_date: 'Start Date',
    end_date: 'End Date',
    time_of_day: 'Time of Day',
    interval: 'Interval',
    chart_date_range: 'Chart Date Range',
    companies: 'Companies',
    business_categories: 'Business Categories',
    schedule_day_friday: 'Friday Availability',
    schedule_day_monday: 'Monday Availability',
    schedule_day_saturday: 'Saturday Availability',
    schedule_day_sunday: 'Sunday Availability',
    schedule_day_thursday: 'Thursday Availability',
    schedule_day_tuesday: 'Tuesday Availability',
    schedule_day_wednesday: 'Wednesday Availability',
    schedule_id: 'Availability Schedule',
    selected_appointment_type: 'Selected Appointment Type',
    send_confirmations_to_prospect: 'Send Email Confirmation',
    skin: 'Desktop Container Theme',
    skin_mobile: 'Mobile Container Theme',
    slot_amount_in_same_time: 'Customer Appointments (Per Time Slot)',
    slot_amount_in_same_time_fri: 'Friday Customer Appointments Override',
    slot_amount_in_same_time_mon: 'Monday Customer Appointments Override',
    slot_amount_in_same_time_overrides_enabled: 'Override the Global Number of Allowed Appointments',
    slot_amount_in_same_time_sat: 'Saturday Customer Appointments Override',
    slot_amount_in_same_time_sun: 'Sunday Customer Appointments Override',
    slot_amount_in_same_time_thu: 'Thursday Customer Appointments Override',
    slot_amount_in_same_time_tue: 'Tuesday Customer Appointments Override',
    slot_amount_in_same_time_wed: 'Wednesday Customer Appointments Override',
    slot_length: 'Time Between Appointments',
    timezone: 'Time Zone',
    slot_remove_after_taken: 'Remove Time Slot When Appointment Is Made',
    slot_taken_status: 'Booked Time Slot Language',
    slots_per_day: 'Appointments Per Day',
    state: 'State',
    disclaimer_hidden: 'Disclaimer',
    time_zone: 'Time Zone',
    website_url: 'Website URL',
    widget_code_css: 'Desktop Custom CSS',
    widget_code_mobile_css: 'Mobile Custom CSS',
    widget_disabled: 'Active Toggle',
    widget_font: 'Font Family',
    widget_zoom_desktop: 'App Zoom - Desktop',
    widget_zoom_mobile: 'App Zoom - Mobile',
    widget_font_size: 'Font Size',
    widget_font_weight: 'Font Weight',
    widget_hidden: 'Demo Mode',
    widget_icons_only_desktop: 'Icons Only Mode For Desktop',
    widget_icons_only_mobile: 'Icons Only Mode For Mobile',
    widget_size: 'App Size',
    widgetbar_expanded: 'Expanded by default',
    widgets_order: 'Desktop App Order',
    widgets_order_mobile: 'Mobile App Order',
    zipcode: 'Zip Code'
};

const appFieldLabelOverrides: Record<string, Record<string, string>> = {
    'Schedule Genie': {
        accepting_influencers_info: 'Referrer Contact'
    },
    Clarity: {
        name: 'Report Name'
    }
};

const appFieldSectionOverrides: Record<string, Record<string, string>> = {
    'Live Chat': {
        call_to_action: 'Widget Settings',
        show_as_standalone: 'Widget Settings',
        show_on_top: 'Widget Settings'
    }
};

const companyFieldSectionMap: Record<string, string> = {
    notification_bar_active: 'Web Notifications',
    notification_bar_background_color: 'Web Notifications',
    notification_bar_color: 'Web Notifications',
    notification_bar_end_date: 'Web Notifications',
    notification_bar_link_1: 'Web Notifications',
    notification_bar_link_2: 'Web Notifications',
    notification_bar_message: 'Web Notifications',
    notification_bar_position: 'Web Notifications',
    notification_bar_start_date: 'Web Notifications',
    meet_elise_enabled: 'MeetElise Widget',
    meet_elise_chat_enabled: 'MeetElise Widget',
    meet_elise_tour_enabled: 'MeetElise Widget',
    meet_elise_email_enabled: 'MeetElise Widget',
    meet_elise_text_enabled: 'MeetElise Widget',
    meet_elise_chat_title: 'MeetElise Widget',
    meet_elise_tour_title: 'MeetElise Widget',
    meet_elise_email_title: 'MeetElise Widget',
    meet_elise_text_title: 'MeetElise Widget'
};

const appFieldSectionMap: Record<string, string> = {
    active: 'App Header',
    accepting_influencers_info: 'App Header',
    appointment_length: 'Scheduling Settings',
    behavior: 'Topic Menu Settings',
    call_to_action: 'Virtual Assistant Settings',
    appointment_types_enabled: 'Available Appointment Types',
    crm_config_id: 'Override Lead Notification Settings',
    description: 'Topic Menu Settings',
    display_name: 'Topic Menu Settings',
    drip_schedule_name: 'Drip Schedules',
    drip_schedule_enabled: 'Drip Schedules',
    drip_entry_added: 'Drip Schedules',
    drip_entry_enabled: 'Drip Schedules',
    drip_entry_time_offset_hours: 'Drip Schedules',
    drip_entry_send_via_email: 'Drip Schedules',
    drip_entry_send_via_sms: 'Drip Schedules',
    drip_entry_template_name: 'Drip Schedules',
    drip_schedule_id: 'Lead Nurturing',
    blast_name: 'Scheduled Blasts',
    blast_send_via_email: 'Scheduled Blasts',
    blast_send_via_sms: 'Scheduled Blasts',
    blast_sms_message: 'Scheduled Blasts',
    blast_email_template_name: 'Scheduled Blasts',
    blast_date: 'Scheduled Blasts',
    blast_time: 'Scheduled Blasts',
    blast_list_segments: 'Scheduled Blasts',
    floorplan_added: 'Floorplans',
    floor_plan_name: 'Floorplans',
    lead_notification_cc_emails: 'Override Lead Notification Settings',
    lead_notification_primary_emails: 'Override Lead Notification Settings',
    market_rent: 'Floorplans',
    max_appointment_period: 'Scheduling Settings',
    greeting: 'Virtual Assistant Settings',
    minimum_income_ratio: 'Default Income Qualification',
    name: 'Topic Menu Settings',
    offer: 'Topic Menu Settings',
    override_lead_notification: 'Override Lead Notification Settings',
    period_start_work: 'Scheduling Settings',
    phone: 'Override Lead Notification Settings',
    reply_to_email: 'Override Lead Notification Settings',
    schedule_created: 'Availability',
    schedule_day_friday: 'Availability',
    schedule_day_monday: 'Availability',
    schedule_day_saturday: 'Availability',
    schedule_day_sunday: 'Availability',
    schedule_day_thursday: 'Availability',
    schedule_day_tuesday: 'Availability',
    schedule_day_wednesday: 'Availability',
    schedule_id: 'Availability',
    selected_appointment_type: 'Available Appointment Types',
    send_confirmations_to_prospect: 'Confirmation Email',
    slot_amount_in_same_time: 'Customer Appointments (per time slot)',
    enabled: 'Topic Menu Settings',
    slot_amount_in_same_time_fri: 'Customer Appointments (per time slot)',
    slot_amount_in_same_time_mon: 'Customer Appointments (per time slot)',
    slot_amount_in_same_time_overrides_enabled: 'Customer Appointments (per time slot)',
    slot_amount_in_same_time_sat: 'Customer Appointments (per time slot)',
    slot_amount_in_same_time_sun: 'Customer Appointments (per time slot)',
    slot_amount_in_same_time_thu: 'Customer Appointments (per time slot)',
    slot_amount_in_same_time_tue: 'Customer Appointments (per time slot)',
    slot_amount_in_same_time_wed: 'Customer Appointments (per time slot)',
    slot_length: 'Scheduling Settings',
    slot_remove_after_taken: 'Scheduling Settings',
    slot_taken_status: 'Scheduling Settings',
    slots_per_day: 'Scheduling Settings',
    sms_message_create: 'Confirmation Text Message',
    list_segment_name: 'List Segments',
    list_segment_enabled: 'List Segments',
    list_segment_contacts_added: 'List Segments',
    list_segment_contacts_imported: 'List Segments'
};

const publicRoleFieldSectionMap: Record<string, string> = {
    widget_font: 'Font Settings',
    widget_font_size: 'Font Settings',
    widget_font_weight: 'Font Settings',
    brand_primary_color: 'Brand Colors',
    brand_text_color: 'Brand Colors',
    widget_icons_only_desktop: 'Style',
    widget_icons_only_mobile: 'Style',
    skin: 'Style',
    skin_mobile: 'Style',
    widget_size: 'Style',
    collapse_threshhold: 'Style',
    collapse_threshhold_mobile: 'Style',
    widgetbar_expanded: 'Style',
    host_element_id: 'Host',
    host_element_type: 'Host',
    host_element_parent: 'Host',
    desktop_position_adjustment: 'Advanced Settings',
    mobile_position_adjustment: 'Advanced Settings',
    widget_code_css: 'Advanced Settings',
    widget_code_mobile_css: 'Advanced Settings',
    widget_zoom_desktop: 'App Design',
    widget_zoom_mobile: 'App Design',
    widgets_order: 'App Orders',
    widgets_order_mobile: 'App Orders',
    hidden_widgets: 'App Orders',
    hidden_widgets_mobile: 'App Orders',
    widget_hidden: 'Preview'
};

const widgetSkinLabelMap: Record<string, string> = {
    default: 'Default',
    skin1: 'Wallet',
    modern: 'Modern'
};

const widgetSizeLabelMap: Record<string, string> = {
    small: 'Small',
    medium: 'Medium',
    large: 'Large'
};

const widgetZoomLabelMap: Record<string, string> = {
    '1': 'Full Size',
    '0.9': '90%',
    '0.8': '80%',
    '0.7': '70%',
    '0.6': '60%'
};

const widgetFontLabelMap: Record<string, string> = {
    inherit: 'Default',
    get_website_fonts: 'Match Website Font Automatically'
};

const widgetModuleLabelMap: Record<string, string> = {
    MODULE_SCHEDULEGENIE: 'Schedule Genie',
    MODULE_CONCESSIONMANAGER: 'Concession Manager',
    MODULE_TEXTUS: 'Text Us',
    MODULE_CALLUS: 'Call Us',
    MODULE_INCOMECALCULATOR: 'Income Calculator',
    MODULE_BPN: 'Spotlight',
    MODULE_CHAT: 'Chat',
    MODULE_CHATBOT: 'Virtual Assistant',
    MODULE_CHATBOT_AI: 'Chatbot AI',
    MODULE_LIVE_CHAT: 'Live Chat',
    MODULE_DYNAMIC: 'Dynamic',
    MODULE_INSITE: 'Insite'
};

const preferredActionCategories = [
    'CRM Configurations',
    'Apps & Features',
    'Customization',
    'Email Templates',
    'Lead Nurturing',
    'Customize & Lead Nurturing',
    'Company Settings',
    'User Management'
];

const clarityMonthlyLeadReportFields = new Set([
    'report_created',
    'name',
    'start_date',
    'end_date',
    'time_of_day',
    'interval',
    'recipients',
    'timezone',
    'chart_date_range',
    'companies',
    'business_categories',
    'recommended_reports'
]);

const legacyActionDetailUnavailableText = 'Update recorded; field-level before/after detail not available.';

const formatFieldLabel = (fieldName: string, appLabel?: string) => {
    if (appLabel && appFieldLabelOverrides[appLabel]?.[fieldName]) {
        return appFieldLabelOverrides[appLabel][fieldName];
    }

    if (fieldLabelMap[fieldName]) {
        return fieldLabelMap[fieldName];
    }

    return fieldName
        .split('_')
        .filter(Boolean)
        .map(token => token.charAt(0).toUpperCase() + token.slice(1))
        .join(' ');
};

const getAppFieldSection = (fieldName: string, appLabel?: string) => {
    if (appLabel && appFieldSectionOverrides[appLabel]?.[fieldName]) {
        return appFieldSectionOverrides[appLabel][fieldName];
    }

    if (appLabel === 'Clarity' && clarityMonthlyLeadReportFields.has(fieldName)) {
        return 'Monthly Lead Report';
    }

    return appFieldSectionMap[fieldName];
};

const parseJsonValue = (value?: unknown) => {
    if (typeof value !== 'string') {
        return value;
    }

    try {
        return JSON.parse(value);
    } catch (_error) {
        return value;
    }
};

const formatPositionAdjustmentValue = (value?: unknown) => {
    const parsedValue = parseJsonValue(value) as Record<string, unknown> | string | null | undefined;

    if (!parsedValue || typeof parsedValue !== 'object' || Array.isArray(parsedValue)) {
        return normalizeString(value as string | number | boolean | null | undefined) || '—';
    }

    return ['top', 'bottom', 'left', 'right']
        .map(key => `${key.charAt(0).toUpperCase() + key.slice(1)}: ${formatAuditValue(parsedValue[key])}`)
        .join(', ');
};

const formatModuleListValue = (value?: unknown) => {
    const parsedValue = parseJsonValue(value);
    const modules = Array.isArray(parsedValue) ? parsedValue : [];

    if (!modules.length) {
        return '—';
    }

    return modules
        .map(moduleCode => widgetModuleLabelMap[`${moduleCode}`] || `${moduleCode}`)
        .join(', ');
};

const emailTemplateLogoLabelMap: Record<string, string> = {
    ada: 'ADA',
    pet: 'Pet',
    eho: 'EHO',
    special_person: 'Special Person'
};

const emailTemplateLinkLabelMap: Record<string, string> = {
    '{{{companyWebsiteLink}}}': 'Link to Homepage',
    '{{{scheduleGenieAppLink}}}': 'Link to Schedule Genie',
    '{{{concessionManagerAppLink}}}': 'Link to Spotlight',
    '{{{bestPriceNowAppLink}}}': 'Link to Best Price Now',
    '{{{textUsNowAppLink}}}': 'Link to Text Us Now',
    '{{{chatAppLink}}}': 'Link to Chat',
    '{{{incomeCalculatorAppLink}}}': 'Link to Income Calculator'
};

const formatHtmlAuditValue = (value?: unknown) => {
    const normalizedValue = normalizeString(value as string | number | boolean | null);

    if (!normalizedValue) {
        return '—';
    }

    return normalizedValue.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() || '—';
};

const formatEmailTemplateLogos = (value?: unknown) => {
    const parsedValue = parseJsonValue(value);
    const logos = Array.isArray(parsedValue) ? parsedValue : [];

    if (!logos.length) {
        return '—';
    }

    return logos.map(logo => emailTemplateLogoLabelMap[`${logo}`] || `${logo}`).join(', ');
};

const formatEmailTemplateBoolean = (value?: unknown) => {
    const normalizedValue = lowerCase(value as string | number | boolean | null);

    if (normalizedValue === 'true') {
        return 'Hidden';
    }

    if (normalizedValue === 'false') {
        return 'Shown';
    }

    return undefined;
};

const formatScheduleGenieBoolean = (value?: unknown, labels = { trueLabel: 'Yes', falseLabel: 'No' }) => {
    const normalizedValue = lowerCase(value as string | number | boolean | null);

    if (normalizedValue === 'true' || normalizedValue === '1') {
        return labels.trueLabel;
    }

    if (normalizedValue === 'false' || normalizedValue === '0') {
        return labels.falseLabel;
    }

    return undefined;
};

const formatScheduleGenieDuration = (value?: unknown) => {
    const numericValue = Number(normalizeString(value as string | number | boolean | null));

    if (Number.isNaN(numericValue)) {
        return `${value}`;
    }

    if (numericValue === 0) {
        return 'Same day';
    }

    return getAppointmentDurationLabels(numericValue).trim();
};

const formatAuditValue = (
    value?: unknown,
    options?: { fieldName?: string; entityType?: AuditLogEntityType }
): string => {
    if (value === null || value === undefined || value === '') {
        return '—';
    }

    if (options?.entityType === 'company' && options.fieldName) {
        const normalizedValue = lowerCase(value as string | number | boolean | null);

        if (options.fieldName === 'plain_text_lead_notify') {
            if (normalizedValue === 'true') {
                return 'Plain Text';
            }
            if (normalizedValue === 'false') {
                return 'HTML';
            }
        }

        if (options.fieldName === 'logo_background_color') {
            if (normalizedValue === 'gray') {
                return 'Gray';
            }
            if (normalizedValue === 'white') {
                return 'White';
            }
        }

        if (options.fieldName === 'notification_bar_active') {
            if (normalizedValue === 'true') {
                return 'Active';
            }
            if (normalizedValue === 'false') {
                return 'Inactive';
            }
        }

        if (options.fieldName === 'notification_bar_position') {
            if (normalizedValue === 'fixed to top') {
                return 'Fixed to Top';
            }
            if (normalizedValue === 'floating') {
                return 'Floating';
            }
        }

        if (options.fieldName === 'meet_elise_enabled') {
            if (normalizedValue === 'true') {
                return 'Active';
            }
            if (normalizedValue === 'false') {
                return 'Inactive';
            }
        }

        if (
            ['meet_elise_chat_enabled', 'meet_elise_tour_enabled', 'meet_elise_email_enabled', 'meet_elise_text_enabled'].includes(
                options.fieldName
            )
        ) {
            if (normalizedValue === 'true') {
                return 'On';
            }
            if (normalizedValue === 'false') {
                return 'Off';
            }
        }

        if (['active', 'is_test', 'is_universal', 'is_paused', 'weekly_notification'].includes(options.fieldName)) {
            if (normalizedValue === 'true') {
                return 'Yes';
            }
            if (normalizedValue === 'false') {
                return 'No';
            }
        }
    }

    if (options?.entityType === 'public_role' && options.fieldName) {
        if (options.fieldName === 'widget_hidden') {
            const normalizedValue = lowerCase(value as string | number | boolean | null);
            if (normalizedValue === 'true') {
                return 'On';
            }
            if (normalizedValue === 'false') {
                return 'Off';
            }
        }

        if (options.fieldName === 'widget_disabled') {
            const normalizedValue = lowerCase(value as string | number | boolean | null);
            if (normalizedValue === '0' || normalizedValue === 'false') {
                return 'Active';
            }
            if (normalizedValue === '1' || normalizedValue === 'true') {
                return 'Inactive';
            }
        }

        if (['widgetbar_expanded', 'widget_icons_only_desktop', 'widget_icons_only_mobile'].includes(options.fieldName)) {
            const normalizedValue = lowerCase(value as string | number | boolean | null);
            if (normalizedValue === 'true') {
                return 'Yes';
            }
            if (normalizedValue === 'false') {
                return 'No';
            }
        }

        if (['skin', 'skin_mobile'].includes(options.fieldName)) {
            return widgetSkinLabelMap[`${value}`] || `${value}`;
        }

        if (options.fieldName === 'widget_size') {
            return widgetSizeLabelMap[`${value}`] || `${value}`;
        }

        if (options.fieldName === 'widget_font') {
            return widgetFontLabelMap[`${value}`] || `${value}`;
        }

        if (options.fieldName === 'widget_zoom_desktop' || options.fieldName === 'widget_zoom_mobile') {
            return widgetZoomLabelMap[`${value}`] || `${value}`;
        }

        if (options.fieldName === 'host_element_type' || options.fieldName === 'host_element_parent') {
            return `${value}`.toUpperCase();
        }

        if (
            ['widgets_order', 'widgets_order_mobile', 'hidden_widgets', 'hidden_widgets_mobile'].includes(options.fieldName)
        ) {
            return formatModuleListValue(value);
        }

        if (['desktop_position_adjustment', 'mobile_position_adjustment'].includes(options.fieldName)) {
            return formatPositionAdjustmentValue(value);
        }
    }

    if (options?.entityType === 'email_template' && options.fieldName) {
        if (
            ['headline_hidden', 'cta_hidden', 'disclaimer_hidden', 'optional_logos_hidden'].includes(options.fieldName)
        ) {
            return formatEmailTemplateBoolean(value) || `${value}`;
        }

        if (options.fieldName === 'optional_logos') {
            return formatEmailTemplateLogos(value);
        }

        if (options.fieldName === 'email_body_html') {
            return formatHtmlAuditValue(value);
        }

        if (options.fieldName === 'cta_link') {
            return emailTemplateLinkLabelMap[`${value}`] || `${value}`;
        }
    }

    if (options?.entityType === 'app' && options.fieldName) {
        const normalizedValue = lowerCase(value as string | number | boolean | null);

        if (['drip_schedule_enabled', 'drip_entry_enabled', 'list_segment_enabled'].includes(options.fieldName)) {
            if (normalizedValue === 'true') {
                return 'Enabled';
            }
            if (normalizedValue === 'false') {
                return 'Disabled';
            }
        }

        if (
            ['drip_entry_send_via_email', 'drip_entry_send_via_sms', 'blast_send_via_email', 'blast_send_via_sms'].includes(
                options.fieldName
            )
        ) {
            if (normalizedValue === 'true') {
                return 'On';
            }
            if (normalizedValue === 'false') {
                return 'Off';
            }
        }

        if (options.fieldName === 'blast_sms_message') {
            return formatHtmlAuditValue(value);
        }

        if (options.fieldName === 'slot_remove_after_taken') {
            return formatScheduleGenieBoolean(value) || `${value}`;
        }

        if (options.fieldName === 'slot_amount_in_same_time_overrides_enabled') {
            return formatScheduleGenieBoolean(value, { trueLabel: 'On', falseLabel: 'Off' }) || `${value}`;
        }

        if (options.fieldName === 'period_start_work') {
            return formatScheduleGenieDuration(value);
        }

        if (['show_as_standalone', 'show_on_top'].includes(options.fieldName)) {
            if (normalizedValue === 'true') {
                return 'Enabled';
            }
            if (normalizedValue === 'false') {
                return 'Disabled';
            }
        }
    }

    if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
    }

    return `${value}`;
};

const buildChangeDetailText = (
    label: string,
    oldValue?: unknown,
    newValue?: unknown,
    options?: { subjectText?: string; formatValue?: (value?: unknown) => string }
) => {
    const formatter = options?.formatValue || formatAuditValue;

    return `Changed ${label}${options?.subjectText ? ` ${options.subjectText}` : ''} from "${formatter(oldValue)}" to "${formatter(
        newValue
    )}"`;
};

const extractLegacyEmailTemplateBodyState = (value?: unknown) => {
    const parsedValue = parseJsonValue(value);

    if (!parsedValue || typeof parsedValue !== 'object' || Array.isArray(parsedValue)) {
        return {} as Record<string, unknown>;
    }

    const sections = parsedValue as Record<string, any>;
    const headlineSection =
        sections['spotlight-headline-section'] || sections['headline-section'] || sections['schedule-genie-headline-section'];
    const disclaimerSection = sections['spotlight-disclaimer-section'];
    const overlaySection =
        sections['spotlight-overlay-section'] || sections['customer-overlay-section'] || sections['schedule-genie-overlay-section'];
    const detailsSection =
        sections['spotlight-details-section'] || sections['customer-details-section'] || sections['details-section'];

    return {
        headline_hidden: headlineSection?.hidden,
        cta_hidden: sections['cta-section']?.hidden,
        disclaimer_hidden: disclaimerSection?.hidden,
        optional_logos_hidden: overlaySection?.hidden,
        optional_logos: overlaySection?.logos,
        banner_image_url: sections['banner-section']?.imageUrl,
        secondary_image_url: sections['logo-section']?.imageUrl,
        email_body_html: detailsSection?.htmlContent
    };
};

const buildLegacyEmailTemplateBodyDetail = (entry: Record<string, unknown>) => {
    const previousState = extractLegacyEmailTemplateBodyState(entry.old_value);
    const nextState = extractLegacyEmailTemplateBodyState(entry.new_value);
    const changedFields = Array.from(new Set([...Object.keys(previousState), ...Object.keys(nextState)]));

    const details = changedFields.reduce((messages, fieldName) => {
        const oldValue = previousState[fieldName];
        const newValue = nextState[fieldName];

        if (JSON.stringify(oldValue ?? null) === JSON.stringify(newValue ?? null)) {
            return messages;
        }

        messages.push(
            buildChangeDetailText(formatFieldLabel(fieldName), oldValue, newValue, {
                formatValue: value => formatAuditValue(value, { fieldName, entityType: 'email_template' })
            })
        );

        return messages;
    }, [] as string[]);

    return details.join(' • ');
};

const getEntryFieldNames = (entry: Record<string, unknown>) => {
    const oldFieldNames = Object.keys(entry)
        .filter(key => key.startsWith('old_') && key !== 'old_id')
        .map(key => key.replace(/^old_/, ''));

    const explicitFieldName = normalizeString(entry.field_name as string | number | boolean | null);

    return Array.from(new Set([explicitFieldName, ...oldFieldNames].filter(Boolean)));
};

const buildDetailFromEntryChanges = (
    entry: Record<string, unknown>,
    options?: { subjectText?: string; appLabel?: string; entityType?: AuditLogEntityType }
) => {
    const explicitDetail =
        normalizeString(entry.action_change_detail as string | number | boolean | null) ||
        normalizeString(entry.change_detail as string | number | boolean | null);

    if (explicitDetail) {
        return explicitDetail;
    }

    const explicitFieldName = normalizeString(entry.field_name as string | number | boolean | null);
    const hasExplicitValuePair = 'old_value' in entry || 'new_value' in entry;

    if (options?.entityType === 'email_template' && explicitFieldName === 'body') {
        const legacyBodyDetail = buildLegacyEmailTemplateBodyDetail(entry);

        if (legacyBodyDetail) {
            return legacyBodyDetail;
        }
    }

    if (explicitFieldName && hasExplicitValuePair) {
        const oldValue = entry.old_value_display ?? entry.old_value;
        const newValue = entry.new_value_display ?? entry.new_value;

        return buildChangeDetailText(formatFieldLabel(explicitFieldName, options?.appLabel), oldValue, newValue, {
            subjectText: options?.subjectText,
            formatValue: value => formatAuditValue(value, { fieldName: explicitFieldName, entityType: options?.entityType })
        });
    }

    const changeDetails = Object.keys(entry)
        .filter(key => key.startsWith('old_') && key !== 'old_id')
        .reduce((changes, oldKey) => {
            const fieldName = oldKey.replace(/^old_/, '');
            const newKey = `new_${fieldName}`;

            if (!(newKey in entry)) {
                return changes;
            }

            const oldValue = entry[oldKey];
            const newValue = entry[newKey];

            if (normalizeString(oldValue as string | number | boolean | null) === normalizeString(newValue as string | number | boolean | null)) {
                return changes;
            }

            changes.push(
                buildChangeDetailText(formatFieldLabel(fieldName, options?.appLabel), oldValue, newValue, {
                    subjectText: options?.subjectText,
                    formatValue: value => formatAuditValue(value, { fieldName, entityType: options?.entityType })
                })
            );
            return changes;
        }, [] as string[]);

    return changeDetails.join(' • ');
};

const inferActionCategory = (
    entityType: AuditLogRow['entityType'],
    entry: Record<string, unknown>,
    operation: string
) => {
    const explicitCategory = normalizeString(entry.action_category as string | number | boolean | null);

    if (explicitCategory) {
        return explicitCategory;
    }

    if (entityType === 'crm_config') {
        return 'CRM Configurations';
    }

    if (entityType === 'app') {
        return 'Apps & Features';
    }

    const searchableText = buildSearchValue(...getEntryFieldNames(entry), operation, entry.change_detail as string | undefined);
    const crmTokens = ['crm', 'provider', 'onesite', 'yardi', 'entrata', 'rentcafe', 'lead2lease'];
    const customizeTokens = [
        'lead_nurturing',
        'drip',
        'blast',
        'email',
        'template',
        'widget',
        'logo',
        'color',
        'banner',
        'style',
        'header',
        'notification_bar'
    ];

    if (crmTokens.some(token => searchableText.includes(token))) {
        return 'CRM Configurations';
    }

    if (customizeTokens.some(token => searchableText.includes(token))) {
        return 'Customize & Lead Nurturing';
    }

    if (entityType === 'company') {
        return 'Company Settings';
    }

    return 'User Management';
};

const appendIdentifier = (label: string, identifier?: string | number) => {
    const normalizedIdentifier = normalizeString(identifier);

    if (!label) {
        return normalizedIdentifier ? `#${normalizedIdentifier}` : '';
    }

    if (!normalizedIdentifier) {
        return label;
    }

    return `${label} (#${normalizedIdentifier})`;
};

const createUserRow = (entry: AuditLogUserEntry): AuditLogRow => {
    const entryRecord = entry as Record<string, unknown>;
    const name = `${normalizeString(entry.old_first_name)} ${normalizeString(entry.old_last_name)}`.trim();
    const actor =
        normalizeString(entry.actor_email) ||
        normalizeString(entry.updated_by_email) ||
        normalizeString(entry.updated_by);
    const targetBase = normalizeString(entry.target_name) || name || normalizeString(entry.old_email) || 'User';
    const target = appendIdentifier(targetBase, entry.target_id || entry.old_id);
    const detailFromChanges = buildDetailFromEntryChanges(entryRecord, {
        subjectText:
            normalizeString(entry.field_name) === 'company_membership' && normalizeString(entry.old_email)
                ? `for ${normalizeString(entry.old_email)}`
                : undefined
    });
    const actionDetail = detailFromChanges || legacyActionDetailUnavailableText;
    const actionCategory = inferActionCategory('user', entryRecord, normalizeString(entry.operation));

    return {
        id: `user-${entry.old_id}-${entry.time_of_action}`,
        entityType: 'user',
        entityLabel: 'Users',
        title: target,
        description: actionDetail,
        timestamp: entry.time_of_action,
        operation: normalizeString(entry.operation),
        updatedBy: actor,
        actor,
        target,
        actionCategory,
        actionDetail,
        rawId: entry.old_id,
        searchValue: buildSearchValue(
            'user',
            actor,
            target,
            actionCategory,
            actionDetail,
            entry.operation,
            entry.old_email,
            entry.old_id
        ),
        actorSearchValue: buildSearchValue(actor),
        targetSearchValue: buildSearchValue(target, entry.old_email, entry.old_id),
        actionCategorySearchValue: buildSearchValue(actionCategory)
    };
};

const createCompanyRow = (entry: AuditLogCompanyEntry): AuditLogRow => {
    const entryRecord = entry as Record<string, unknown>;
    const actor =
        normalizeString(entry.actor_email) ||
        normalizeString(entry.updated_by_email) ||
        normalizeString(entry.updated_by);
    const targetBase = normalizeString(entry.target_name) || normalizeString(entry.old_name) || 'Company';
    const target = appendIdentifier(targetBase, entry.target_id || entry.old_id);
    const fieldName = normalizeString(entry.field_name);
    const sectionName = companyFieldSectionMap[fieldName];
    const detailFromChanges = buildDetailFromEntryChanges(entryRecord, {
        entityType: 'company',
        subjectText: sectionName ? `in ${sectionName}` : undefined
    });
    const actionDetail = detailFromChanges || legacyActionDetailUnavailableText;
    const actionCategory = inferActionCategory('company', entryRecord, normalizeString(entry.operation));

    return {
        id: `company-${entry.old_id}-${entry.time_of_action}`,
        entityType: 'company',
        entityLabel: 'Companies',
        title: target,
        description: actionDetail,
        timestamp: entry.time_of_action,
        operation: normalizeString(entry.operation),
        updatedBy: actor,
        actor,
        target,
        actionCategory,
        actionDetail,
        rawId: entry.old_id,
        searchValue: buildSearchValue(
            'company',
            actor,
            target,
            actionCategory,
            sectionName,
            actionDetail,
            entry.operation,
            entry.field_name,
            entry.old_value,
            entry.new_value,
            entry.old_id
        ),
        actorSearchValue: buildSearchValue(actor),
        targetSearchValue: buildSearchValue(target, entry.old_name, entry.old_id),
        actionCategorySearchValue: buildSearchValue(actionCategory)
    };
};

const createAppRow = (entry: AuditLogAppEntry): AuditLogRow => {
    const entryRecord = entry as Record<string, unknown>;
    const actor =
        normalizeString(entry.actor_email) ||
        normalizeString(entry.updated_by_email) ||
        normalizeString(entry.updated_by);
    const targetBase = normalizeString(entry.target_name) || normalizeString(entry.company_name) || 'Client / Property';
    const target = appendIdentifier(targetBase, entry.target_id);
    const fieldName = normalizeString(entry.field_name);
    const appLabel = normalizeString(entry.feature_name);
    const fieldSection = getAppFieldSection(fieldName, appLabel);
    const detailFromChanges = buildDetailFromEntryChanges(entryRecord, {
        subjectText: fieldSection ? `in ${fieldSection}` : undefined,
        appLabel,
        entityType: 'app'
    });
    const operation = normalizeString(entry.operation);
    const isFeatureToggleRow = !entry.app_id && ['ENABLE', 'DISABLE'].includes(operation);
    const baseActionDetail = isFeatureToggleRow
        ? operation === 'ENABLE'
            ? 'Enabled'
            : 'Disabled'
        : detailFromChanges || legacyActionDetailUnavailableText;
    const actionDetail = appLabel ? `[${appLabel}] ${baseActionDetail}` : baseActionDetail;
    const actionCategory = inferActionCategory('app', entryRecord, operation);

    return {
        id: `app-${entry.id}-${entry.time_of_action}`,
        entityType: 'app',
        entityLabel: 'Apps',
        title: target,
        description: actionDetail,
        timestamp: entry.time_of_action,
        operation,
        updatedBy: actor,
        actor,
        target,
        actionCategory,
        actionDetail,
        active: entry.active,
        rawId: entry.id,
        searchValue: buildSearchValue(
            'app',
            actor,
            target,
            actionCategory,
            actionDetail,
            entry.operation,
            entry.id,
            entry.app_id,
            entry.company_name,
            entry.feature_name
        ),
        actorSearchValue: buildSearchValue(actor),
        targetSearchValue: buildSearchValue(target, entry.company_name, entry.id, entry.app_id),
        actionCategorySearchValue: buildSearchValue(actionCategory)
    };
};

const createPublicRoleRow = (entry: AuditLogPublicRoleEntry): AuditLogRow => {
    const entryRecord = entry as Record<string, unknown>;
    const actor =
        normalizeString(entry.actor_email) ||
        normalizeString(entry.updated_by_email) ||
        normalizeString(entry.updated_by);
    const targetBase = normalizeString(entry.target_name) || 'Client / Property';
    const target = appendIdentifier(targetBase, entry.target_id || entry.company_id);
    const fieldName = normalizeString(entry.field_name);
    const sectionName = publicRoleFieldSectionMap[fieldName];
    const detailFromChanges = buildDetailFromEntryChanges(entryRecord, {
        subjectText: sectionName ? `in ${sectionName}` : undefined,
        entityType: 'public_role'
    });
    const actionDetail = detailFromChanges || legacyActionDetailUnavailableText;
    const actionCategory = inferActionCategory('public_role', entryRecord, normalizeString(entry.operation));

    return {
        id: `public-role-${entry.id}-${entry.time_of_action}`,
        entityType: 'public_role',
        entityLabel: 'Customize',
        title: target,
        description: actionDetail,
        timestamp: entry.time_of_action,
        operation: normalizeString(entry.operation),
        updatedBy: actor,
        actor,
        target,
        actionCategory,
        actionDetail,
        rawId: entry.id,
        searchValue: buildSearchValue(
            'customize',
            actor,
            target,
            actionCategory,
            sectionName,
            actionDetail,
            entry.operation,
            entry.field_name,
            entry.old_value,
            entry.new_value
        ),
        actorSearchValue: buildSearchValue(actor),
        targetSearchValue: buildSearchValue(target, entry.target_name, entry.target_id, entry.company_id),
        actionCategorySearchValue: buildSearchValue(actionCategory)
    };
};

const createEmailTemplateRow = (entry: AuditLogEmailTemplateEntry): AuditLogRow => {
    const entryRecord = entry as Record<string, unknown>;
    const actor =
        normalizeString(entry.actor_email) ||
        normalizeString(entry.updated_by_email) ||
        normalizeString(entry.updated_by);
    const targetBase = normalizeString(entry.target_name) || 'Client / Property';
    const target = appendIdentifier(targetBase, entry.target_id || entry.company_id);
    const detailFromChanges = buildDetailFromEntryChanges(entryRecord, { entityType: 'email_template' });
    const baseActionDetail = detailFromChanges || legacyActionDetailUnavailableText;
    const templateLabel = normalizeString(entry.template_id);
    const actionDetail = templateLabel ? `[${templateLabel}] ${baseActionDetail}` : baseActionDetail;
    const actionCategory = inferActionCategory('email_template', entryRecord, normalizeString(entry.operation));

    return {
        id: `email-template-${entry.id}-${entry.time_of_action}`,
        entityType: 'email_template',
        entityLabel: 'Email Templates',
        title: target,
        description: actionDetail,
        timestamp: entry.time_of_action,
        operation: normalizeString(entry.operation),
        updatedBy: actor,
        actor,
        target,
        actionCategory,
        actionDetail,
        rawId: entry.id,
        searchValue: buildSearchValue(
            'email template',
            actor,
            target,
            templateLabel,
            actionCategory,
            actionDetail,
            entry.operation,
            entry.field_name,
            entry.old_value,
            entry.new_value
        ),
        actorSearchValue: buildSearchValue(actor),
        targetSearchValue: buildSearchValue(target, entry.target_name, entry.target_id, entry.company_id),
        actionCategorySearchValue: buildSearchValue(actionCategory)
    };
};

const createCrmConfigRow = (entry: AuditLogCrmConfigEntry): AuditLogRow => {
    const entryRecord = entry as Record<string, unknown>;
    const actor =
        normalizeString(entry.actor_email) ||
        normalizeString(entry.updated_by_email) ||
        normalizeString(entry.updated_by);
    const targetBase =
        normalizeString(entry.target_name) || normalizeString(entry.company_name) || 'Client / Property';
    const target = appendIdentifier(targetBase, entry.target_id ?? entry.company_id);
    const detailFromChanges = buildDetailFromEntryChanges(entryRecord);
    const actionDetail = detailFromChanges || legacyActionDetailUnavailableText;
    const actionCategory = inferActionCategory('crm_config', entryRecord, normalizeString(entry.operation));
    const crmTitle = [normalizeString(entry.crm_type), normalizeString(entry.name)].filter(Boolean).join(' — ');

    return {
        id: `crm-${entry.id}-${entry.time_of_action}`,
        entityType: 'crm_config',
        entityLabel: 'CRM Configurations',
        title: crmTitle || target,
        description: actionDetail,
        timestamp: entry.time_of_action,
        operation: normalizeString(entry.operation),
        updatedBy: actor,
        actor,
        target,
        actionCategory,
        actionDetail,
        rawId: entry.id,
        searchValue: buildSearchValue(
            'crm',
            actor,
            target,
            actionCategory,
            actionDetail,
            entry.operation,
            entry.crm_type,
            entry.name,
            entry.field_name,
            entry.company_name
        ),
        actorSearchValue: buildSearchValue(actor),
        targetSearchValue: buildSearchValue(target, entry.company_name, entry.crm_config_id),
        actionCategorySearchValue: buildSearchValue(actionCategory)
    };
};

export const normalizeAuditLogs = (data: AuditLogDashboardData): AuditLogRow[] => {
    const users = (data.users || []).map(createUserRow);
    const companies = (data.companies || []).map(createCompanyRow);
    const apps = (data.apps || []).map(createAppRow);
    const crmConfigs = (data.crmConfigs || []).map(createCrmConfigRow);
    const publicRoles = (data.publicRoles || []).map(createPublicRoleRow);
    const emailTemplates = (data.emailTemplates || []).map(createEmailTemplateRow);

    return [...users, ...companies, ...apps, ...crmConfigs, ...publicRoles, ...emailTemplates].sort(
        (left, right) => new Date(right.timestamp).getTime() - new Date(left.timestamp).getTime()
    );
};

const isWithinDateRange = (timestamp: string, dateFrom?: string, dateTo?: string) => {
    const value = new Date(timestamp).getTime();

    if (Number.isNaN(value)) {
        return false;
    }

    if (dateFrom) {
        const from = new Date(`${dateFrom}T00:00:00`).getTime();
        if (!Number.isNaN(from) && value < from) {
            return false;
        }
    }

    if (dateTo) {
        const to = new Date(`${dateTo}T23:59:59.999`).getTime();
        if (!Number.isNaN(to) && value > to) {
            return false;
        }
    }

    return true;
};

export const applyAuditLogFilters = (rows: AuditLogRow[], filters: AuditLogFilters): AuditLogRow[] => {
    const searchText = lowerCase(filters.searchText);
    const actorEmail = lowerCase(filters.actorEmail);
    const targetText = lowerCase(filters.targetText);
    const actionCategory = lowerCase(filters.actionCategory);

    return rows.filter(row => {
        if (actionCategory && actionCategory !== 'all' && lowerCase(row.actionCategory) !== actionCategory) {
            return false;
        }

        if (actorEmail && !row.actorSearchValue.includes(actorEmail)) {
            return false;
        }

        if (targetText && !row.targetSearchValue.includes(targetText)) {
            return false;
        }

        if (searchText && !row.searchValue.includes(searchText)) {
            return false;
        }

        if (!isWithinDateRange(row.timestamp, filters.dateFrom, filters.dateTo)) {
            return false;
        }

        return true;
    });
};

export const getAuditLogActionCategoryOptions = (rows: AuditLogRow[]) => {
    const uniqueCategories = Array.from(new Set(rows.map(row => normalizeString(row.actionCategory)).filter(Boolean)));
    const preferredCategoriesInResults = preferredActionCategories.filter(category => uniqueCategories.includes(category));
    const remainingCategories = uniqueCategories
        .filter(category => !preferredCategoriesInResults.includes(category))
        .sort((left, right) => left.localeCompare(right));

    return [...preferredCategoriesInResults, ...remainingCategories];
};

export const getDefaultAuditLogFilters = (): AuditLogFilters => ({
    searchText: '',
    actorEmail: '',
    targetText: '',
    actionCategory: 'all',
    dateFrom: '',
    dateTo: ''
});
